import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';

export interface AgentSummary {
  id: string;
  agent_id: string;
  agent_name: string | null;
  agent_type: string | null;
  status: 'active' | 'idle' | 'error' | 'stopped' | 'planned';
  mode: 'propose' | 'autonomous';
  version: string | null;
  category: 'python_agent' | 'claude_persona' | 'mcp_tool' | 'celery_task' | null;
  description: string | null;
  capabilities: string[] | null;
  trigger_type: string | null;
  parent_agent_id: string | null;
  tool_ref: string | null;
  launch_command: string | null;
  last_action_at: string | null;
  config_json: Record<string, unknown> | null;
  last_action_type: string | null;
  last_action_result: string | null;
  last_action_created_at: string | null;
  children: AgentSummary[];
}

export interface AgentConfig {
  [key: string]: unknown;
}

export interface AgentPrompt {
  id: string;
  agent_id: string;
  task_type: string;
  prompt_text: string;
  version: number;
  is_active: boolean;
  created_at: string;
  notes: string | null;
  author: string | null;
}

export interface AgentAction {
  id: string;
  agent_id: string;
  action_type: string;
  entity_type: string | null;
  entity_id: string | null;
  result: string | null;
  created_at: string;
}

export const agentsAdminService = {
  async listAgents(): Promise<AgentSummary[]> {
    return apiClient.get(API_ENDPOINTS.projectAgents);
  },

  async getConfig(agentId: string): Promise<{ config: AgentConfig }> {
    return apiClient.get(API_ENDPOINTS.agentConfig(agentId));
  },

  async patchConfig(agentId: string, config: AgentConfig): Promise<{ config: AgentConfig }> {
    return apiClient.patch(API_ENDPOINTS.agentConfig(agentId), { config });
  },

  async listPrompts(agentId: string, taskType?: string): Promise<AgentPrompt[]> {
    return apiClient.get(API_ENDPOINTS.agentPrompts(agentId, taskType));
  },

  async getActivePrompts(agentId: string): Promise<AgentPrompt[]> {
    return apiClient.get(API_ENDPOINTS.agentPromptsActive(agentId));
  },

  async createPrompt(
    agentId: string,
    payload: { task_type: string; prompt_text: string; notes?: string; author?: string },
  ): Promise<{ id: string; version: number; is_active: boolean }> {
    return apiClient.post(API_ENDPOINTS.agentPrompts(agentId), payload);
  },

  async activatePrompt(agentId: string, promptId: string): Promise<{ status: string }> {
    return apiClient.patch(API_ENDPOINTS.agentPromptActivate(agentId, promptId), {});
  },

  async listActions(agentId: string, limit = 20): Promise<AgentAction[]> {
    return apiClient.get(API_ENDPOINTS.agentActions(agentId, limit));
  },

  async patchMode(agentId: string, mode: 'propose' | 'autonomous'): Promise<{ agent_id: string; mode: string }> {
    return apiClient.patch(API_ENDPOINTS.agentMode(agentId), { mode });
  },
};
